import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  type: string;
  websiteUrl?: string;
}

interface SavedAccount {
  token: string;
  user: User;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  savedAccounts: SavedAccount[];
  switchAccount: (email: string) => void;
  removeAccount: (email: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>([]);

  // Load auth state and saved accounts from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    const storedAccounts = localStorage.getItem('savedAccounts');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
    }

    if (storedAccounts) {
      try {
        const parsedAccounts = JSON.parse(storedAccounts);
        setSavedAccounts(parsedAccounts);
      } catch (error) {
        console.error('Failed to parse saved accounts:', error);
        localStorage.removeItem('savedAccounts');
      }
    }

    setIsLoading(false);
  }, []);

  const login = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('authUser', JSON.stringify(newUser));

    // Save account to savedAccounts list (if not already there)
    setSavedAccounts(prev => {
      const existingIndex = prev.findIndex(acc => acc.user.email === newUser.email);
      let updated: SavedAccount[];
      if (existingIndex >= 0) {
        // Update existing account with new token
        updated = [...prev];
        updated[existingIndex] = { token: newToken, user: newUser };
      } else {
        // Add new account
        updated = [...prev, { token: newToken, user: newUser }];
      }
      localStorage.setItem('savedAccounts', JSON.stringify(updated));
      return updated;
    });
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    // Note: savedAccounts are NOT removed on logout - they persist for quick switching
  };

  const switchAccount = (email: string) => {
    const account = savedAccounts.find(acc => acc.user.email === email);
    if (account) {
      setToken(account.token);
      setUser(account.user);
      localStorage.setItem('authToken', account.token);
      localStorage.setItem('authUser', JSON.stringify(account.user));
    }
  };

  const removeAccount = (email: string) => {
    setSavedAccounts(prev => {
      const updated = prev.filter(acc => acc.user.email !== email);
      localStorage.setItem('savedAccounts', JSON.stringify(updated));
      return updated;
    });
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    isLoading,
    savedAccounts,
    switchAccount,
    removeAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}